import React from 'react';
import { useLocalization } from '@/Hooks';
import styles from './AdditionalInfo.scss';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { purchasesPageSelectors } from '@/Pages/Purchases/services';
import { salesAdditionalInfoSelectors } from './services/selectors';
import { Label } from '@/Components/Controls/Label/Label';
import { dateTimeUtils } from '@/Utils';
import { FormValue } from '@/Components/Controls/FormValue/FormValue';
import { FormMultiLineValue } from '@/Components/Controls/FormMultiLineValue/FormMultiLineValue';
import { Checkbox } from '@/Components/Controls/Checkbox/Checkbox';
import { orderTableRowUtils } from '@/Types/OrderTableRowUtils';

export const AdditionalInfo: React.FC = () => {
  const {
    inputLabels,
    salesPage: { AdditionalInfo },
  } = useLocalization();

  const selectedPurchase = useSelector(purchasesPageSelectors.selectedPurchase);
  const totalPurchaseNet = useSelector(salesAdditionalInfoSelectors.totalPurchaseNet);

  const saleFormat = selectedPurchase?.orderItems
    .map((i) => i.saleFormat)
    .filter((val, index, self) => self.indexOf(val) === index)
    .join(', ');

  const warehousePlace = selectedPurchase?.orderItems
    .map((i) => i.warehousePlace)
    .filter(Boolean)
    .join(', ');

  const sku = selectedPurchase?.orderItems
    .map((i) => i.sku)
    .filter(Boolean)
    .join(', ');

  return (
    <section className={styles.form}>
      <section className={styles.form__section}>
        <Label text={AdditionalInfo.SalesFormat} className={styles.form__label}>
          <FormValue value={saleFormat} />
        </Label>
        <Label text={AdditionalInfo.PaymentMethod} className={styles.form__label}>
          <FormValue value={selectedPurchase?.paymentMethod} />
        </Label>
        <Label text={AdditionalInfo.OriginalShippingMethod} className={styles.form__label}>
          <FormValue value={selectedPurchase?.shippingMethodDescription} />
        </Label>
      </section>

      <section className={styles.form__section}>
        <Label text={AdditionalInfo.PurchaseNet} className={styles.form__label}>
          <FormValue value={totalPurchaseNet} />
        </Label>
        <Label text={AdditionalInfo.MarketplaceFee} className={styles.form__label}>
          <FormValue value={selectedPurchase?.marketplaceFee} />
        </Label>
        <Label text={AdditionalInfo.PromotionFee} className={styles.form__label}>
          <FormValue value={orderTableRowUtils.calculateOrderPromotionFee(selectedPurchase).toCurrencyString()} />
        </Label>

        <Label text={AdditionalInfo.PaymentFee} className={styles.form__label}>
          <FormValue value={''} />
        </Label>

        <Label text={AdditionalInfo.Shipping} className={styles.form__label}>
          <FormValue value={selectedPurchase?.shippingPrice} />
        </Label>
        <Label text={AdditionalInfo.Profit} className={styles.form__label}>
          <FormValue value={selectedPurchase?.profit} />
        </Label>
      </section>

      <section className={styles.form__section}>
        <Label text={AdditionalInfo.WarehouseId} className={cn(styles.form__label, styles.form__label_multiline)}>
          <FormMultiLineValue value={warehousePlace} className={styles.maxHeight} />
        </Label>
        <Label text={inputLabels.Sku} className={cn(styles.form__label, styles.form__label_multiline)}>
          <FormMultiLineValue value={sku} className={styles.maxHeight} />
        </Label>
      </section>

      <section className={styles.form__section}>
        <Label text={AdditionalInfo.LastModifiedDate} className={styles.form__label}>
          <FormValue value={dateTimeUtils.utcStringToLocalString(selectedPurchase?.mpLastModifiedTime)} />
        </Label>
        <Label text={AdditionalInfo.LastSynchDate} className={styles.form__label}>
          <FormValue value={dateTimeUtils.utcStringToLocalString(selectedPurchase?.synchTime)} />
        </Label>
        <Label text={AdditionalInfo.Archived} className={cn(styles.form__label, styles.form__label_checkbox)}>
          <Checkbox valueForSet={Boolean(selectedPurchase?.archived)} />
        </Label>
        <Label text={AdditionalInfo.Exported} className={cn(styles.form__label, styles.form__label_checkbox)}>
          <Checkbox valueForSet={Boolean(selectedPurchase?.exported)} />
        </Label>
        <Label text={AdditionalInfo.Canceled} className={cn(styles.form__label, styles.form__label_checkbox)}>
          <Checkbox valueForSet={orderTableRowUtils.isOrderCanceled(selectedPurchase)} />
        </Label>
      </section>
    </section>
  );
};
